import { Injectable, inject } from '@angular/core';
import { FileReferenceDetails, FileReferenceDocument } from '@formbird/types';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { NotificationService } from '../notification/notification.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';

@Injectable({
  providedIn: 'root'
})
export class OpfsCacheService {
  loggedInUserService = inject(LoggedInUserService);
  private notificationService = inject(NotificationService);
  private offlineStatusService = inject(OfflineStatusService);

  constructor(
  ) { }

  async cacheFile(file: Pick<FileReferenceDetails, 'fileNo'>, blob: Blob) {
    const opfsRoot = await navigator.storage.getDirectory();
    const dirName = this.getDirectoryName();
    const filesDir = await opfsRoot.getDirectoryHandle(dirName, { create: true });
    if (await this.getCachedFile(file.fileNo) !== null) {
      console.warn('File already exists in cache, overwriting', file.fileNo);
    } else {
      // Only increment if the file is not already in the cache
      this.offlineStatusService.incPendingUploadCount();
    }
    const fileHandle = await filesDir.getFileHandle(`${file.fileNo}`, { create: true });
    const writeable = await fileHandle.createWritable();
    await writeable.write(blob);
    await writeable.close();
  }

  async clearData() {
    try {
      this.notificationService.info("Clearing offline files in the background...");
      const dirName = this.getDirectoryName();

      const userAgent = navigator.userAgent;
      if (/android/i.test(userAgent) && (/chrome/i.test(userAgent))) {
        const opfsRoot = await navigator.storage.getDirectory();
        opfsRoot.removeEntry(dirName, { recursive: true });
        this.offlineStatusService.setPendingUploadCount(0);
      } else {
        const sharedWorker = new SharedWorker('opfs-cache.worker.js');

        const result = await new Promise<{ success: boolean; message: string }>((resolve, reject) => {
          sharedWorker.port.onmessage = (event) => {
            resolve(event.data);
          };

          sharedWorker.onerror = (error) => {
            console.error('Shared Worker Error:', error.message);
            reject(new Error('An error occurred in the Shared Worker.'));
          };

          sharedWorker.port.start();
          sharedWorker.port.postMessage({ dirName });
        });

        if (result.success) {
          this.notificationService.success(result.message);
          this.offlineStatusService.setPendingUploadCount(0);
        } else {
          this.notificationService.error("Oops! Something went wrong while clearing the offline files.");
          console.error(result.message);
        }
      }
    } catch (err) {
      this.notificationService.error("Oops! Something went wrong while clearing the offline files.");
      console.error(err.message);
    }
  }

  async getCachedFile(fileNo: string) {
    const opfsRoot = await navigator.storage.getDirectory();
    try {
      const dirName = this.getDirectoryName();
      const filesDir = await opfsRoot.getDirectoryHandle(dirName, { create: false });
      const fileHandle = await filesDir.getFileHandle(fileNo);
      return await fileHandle.getFile();
    } catch (e) {
      if (e.name === 'NotFoundError') {
        return null;
      } else {
        throw e;
      }
    }
  }

  getDirectoryName = () => {
    return `files-${this.loggedInUserService.getUserId()}`;
  }

}
